import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function PartnersPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [mobileSocialsOpen, setMobileSocialsOpen] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    businessType: '',
    location: '',
    discountPercent: '',
    description: '',
    socialFollowing: '',
    whyPartner: '',
    instagramHandle: '',
    tiktokHandle: '',
    youtubeChannel: '',
    facebookPage: '',
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [consentToContact, setConsentToContact] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consentToContact) {
      setFormStatus('error');
      return;
    }
    
    setFormStatus('loading');

    try {
      // Combine additional info into message field
      const messageContent = [
        `Business Description: ${formData.description}`,
        `Why Partner: ${formData.whyPartner}`,
        formData.discountPercent ? `Proposed Discount: ${formData.discountPercent}` : '',
        formData.socialFollowing ? `Social Following: ${formData.socialFollowing}` : '',
        formData.instagramHandle ? `Instagram: ${formData.instagramHandle}` : '',
        formData.tiktokHandle ? `TikTok: ${formData.tiktokHandle}` : '',
        formData.youtubeChannel ? `YouTube: ${formData.youtubeChannel}` : '',
        formData.facebookPage ? `Facebook: ${formData.facebookPage}` : '',
      ].filter(Boolean).join('\n\n');

      const { error } = await supabase
        .from('partner_applications')
        .insert({
          business_name: formData.businessName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          business_type: formData.businessType,
          location: formData.location,
          website: formData.website || null,
          message: messageContent,
          status: 'pending'
        });

      if (error) throw error;

      setFormStatus('success');
      setFormData({
        businessName: '',
        contactName: '',
        email: '',
        phone: '',
        website: '',
        businessType: '',
        location: '',
        discountPercent: '',
        description: '',
        socialFollowing: '',
        whyPartner: '',
        instagramHandle: '',
        tiktokHandle: '',
        youtubeChannel: '',
        facebookPage: '',
      });
      setConsentToContact(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black' : 'bg-black/40'}`}>
        <div className="flex items-center justify-between h-20 md:h-24 px-4 md:px-8">
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/1d9414342dd58a00fdde5ee22dbe4d3e.png" 
              alt="Rides & Dives Logo" 
              className="h-16 md:h-20 w-auto"
            />
          </Link>
          
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white text-2xl cursor-pointer"
          >
            <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>

          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            <Link to="/rides" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Rides</Link>
            <Link to="/dives" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Dives</Link>
            <Link to="/gear" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Gear</Link>
            <Link to="/gallery" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Gallery</Link>
            <Link to="/merch" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Merch</Link>
            <Link to="/trip-planner" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Plan</Link>
            <div 
              className="relative"
              onMouseEnter={() => setSocialsOpen(true)}
              onMouseLeave={() => setSocialsOpen(false)}
            >
              <button 
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                Follow
                <i className={`ri-arrow-down-s-line transition-transform ${socialsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {socialsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="bg-black border border-white/10 py-2 min-w-[160px]">
                    <a href="https://www.youtube.com/@RidesDives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-youtube-fill text-lg"></i>
                      YouTube
                    </a>
                    <a href="https://www.instagram.com/rides.and.dives/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-instagram-fill text-lg"></i>
                      Instagram
                    </a>
                    <a href="https://www.tiktok.com/@ridesanddives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-tiktok-fill text-lg"></i>
                      TikTok
                    </a>
                    <a href="https://www.facebook.com/share/g/1AqygJbGTi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-facebook-fill text-lg"></i>
                      Facebook
                    </a>
                    <Link to="/blog" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-article-line text-lg"></i>
                      Blog
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <Link to="/about" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer">About</Link>
            <Link to="/contact" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer">Contact</Link>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-black border-t border-white/10">
            <div className="flex flex-col py-4 px-4 space-y-4">
              <Link to="/rides" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Rides</Link>
              <Link to="/dives" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Dives</Link>
              <Link to="/gear" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Gear</Link>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Gallery</Link>
              <Link to="/merch" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Merch</Link>
              <Link to="/trip-planner" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Plan</Link>
              <div>
                <button 
                  onClick={() => setMobileSocialsOpen(!mobileSocialsOpen)}
                  className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2 flex items-center gap-1 w-full"
                >
                  Follow
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileSocialsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileSocialsOpen && (
                  <div className="pl-4 space-y-3 mt-2">
                    <a href="https://www.youtube.com/@RidesDives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-youtube-fill text-lg"></i>
                      YouTube
                    </a>
                    <a href="https://www.instagram.com/rides.and.dives/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-instagram-fill text-lg"></i>
                      Instagram
                    </a>
                    <a href="https://www.tiktok.com/@ridesanddives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-tiktok-fill text-lg"></i>
                      TikTok
                    </a>
                    <a href="https://www.facebook.com/share/g/1AqygJbGTi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-facebook-fill text-lg"></i>
                      Facebook
                    </a>
                    <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-article-line text-lg"></i>
                      Blog
                    </Link>
                  </div>
                )}
              </div>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">About</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-end pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/50fcec2333f8e1bc0a47555226e6253b.jpeg"
            alt="Motorcycle riders on mountain road"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        </div>
        <div className="relative z-10 px-4 md:px-16 max-w-4xl">
          <p className="text-[#8B6F47] text-xs tracking-[3px] uppercase mb-4">Join the Journey</p>
          <h1 className="font-serif text-4xl md:text-7xl font-bold leading-tight mb-4 md:mb-6">
            RIDE WITH US
          </h1>
          <p className="text-lg md:text-2xl font-light text-white/80 mb-6">
            Partner with Rides & Dives and connect with thousands of passionate riders exploring the best roads and dive bars across America.
          </p>
          <div className="w-20 h-0.5 bg-[#8B6F47]"></div>
        </div>
      </section>

      {/* Benefits Section */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-center mb-16">Why Partner With Us</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <i className="ri-group-line text-6xl text-[#8B6F47]"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Engaged Community</h3>
            <p className="text-white/70">
              Connect with thousands of active motorcycle enthusiasts exploring mountain roads
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <i className="ri-map-pin-line text-6xl text-[#8B6F47]"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Featured Placement</h3>
            <p className="text-white/70">
              Get featured in our partner directory and route recommendations
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <i className="ri-star-line text-6xl text-[#8B6F47]"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Exclusive Rewards</h3>
            <p className="text-white/70">
              Offer special discounts and rewards to our loyal community members
            </p>
          </div>
        </div>
      </div>

      {/* Application Form Section */}
      <div className="bg-black py-20">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-center mb-4 text-white">Apply to Become a Partner</h2>
          <p className="text-center text-white/70 mb-12">
            Fill out the form below and we'll get back to you within 48 hours
          </p>

          {formStatus === 'success' && (
            <div className="mb-8 p-6 bg-green-900/30 border border-green-500 rounded-lg text-center">
              <i className="ri-checkbox-circle-line text-4xl text-green-500 mb-2"></i>
              <p className="text-green-400 text-lg font-semibold">
                Application Submitted Successfully!
              </p>
              <p className="text-white/70 mt-2">
                We'll review your application and get back to you soon.
              </p>
            </div>
          )}

          {formStatus === 'error' && (
            <div className="mb-8 p-6 bg-red-900/30 border border-red-500 rounded-lg text-center">
              <i className="ri-error-warning-line text-4xl text-red-500 mb-2"></i>
              <p className="text-red-400 text-lg font-semibold">Submission Failed</p>
              <p className="text-white/70 mt-2">Please try again or contact us directly.</p>
            </div>
          )}

          <form
            id="partner-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium mb-2 text-white">
                Business Name *
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white"
              />
            </div>

            <div>
              <label htmlFor="contactName" className="block text-sm font-medium mb-2 text-white">
                Contact Name *
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2 text-white">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2 text-white">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white"
                />
              </div>
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium mb-2 text-white">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white"
              />
            </div>

            <div>
              <label htmlFor="businessType" className="block text-sm font-medium mb-2 text-white">
                Business Type *
              </label>
              <select
                id="businessType"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white appearance-none cursor-pointer"
              >
                <option value="">Select a type</option>
                <option value="bar-restaurant">Bar/Restaurant</option>
                <option value="hotel-lodging">Hotel/Lodging</option>
                <option value="gear-shop">Gear Shop</option>
                <option value="motorcycle-dealer">Motorcycle Dealer</option>
                <option value="repair-service">Repair/Service</option>
                <option value="tour-operator">Tour Operator</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium mb-2 text-white">
                Location *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="City, State"
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
              />
            </div>

            <div>
              <label htmlFor="discountPercent" className="block text-sm font-medium mb-2 text-white">
                Proposed Discount for Members (%)
              </label>
              <input
                type="text"
                id="discountPercent"
                name="discountPercent"
                value={formData.discountPercent}
                onChange={handleChange}
                placeholder="e.g., 10% off food and drinks"
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium mb-2 text-white">
                Business Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                maxLength={500}
                placeholder="Tell us about your business..."
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white resize-none placeholder:text-white/40"
              ></textarea>
              <p className="text-sm text-white/50 mt-1">
                {formData.description.length}/500 characters
              </p>
            </div>

            <div className="pt-6 border-t border-[#8B6F47]/30">
              <h3 className="text-xl font-bold mb-4 text-white">Social Media (Optional)</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="instagramHandle" className="block text-sm font-medium mb-2 text-white">
                    Instagram Handle
                  </label>
                  <input
                    type="text"
                    id="instagramHandle"
                    name="instagramHandle"
                    value={formData.instagramHandle}
                    onChange={handleChange}
                    placeholder="@yourbusiness"
                    className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
                  />
                </div>

                <div>
                  <label htmlFor="tiktokHandle" className="block text-sm font-medium mb-2 text-white">
                    TikTok Handle
                  </label>
                  <input
                    type="text"
                    id="tiktokHandle"
                    name="tiktokHandle"
                    value={formData.tiktokHandle}
                    onChange={handleChange}
                    placeholder="@yourbusiness"
                    className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
                  />
                </div>

                <div>
                  <label htmlFor="youtubeChannel" className="block text-sm font-medium mb-2 text-white">
                    YouTube Channel
                  </label>
                  <input
                    type="text"
                    id="youtubeChannel"
                    name="youtubeChannel"
                    value={formData.youtubeChannel}
                    onChange={handleChange}
                    placeholder="Channel URL or name"
                    className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
                  />
                </div>

                <div>
                  <label htmlFor="facebookPage" className="block text-sm font-medium mb-2 text-white">
                    Facebook Page
                  </label>
                  <input
                    type="text"
                    id="facebookPage"
                    name="facebookPage"
                    value={formData.facebookPage}
                    onChange={handleChange}
                    placeholder="Page URL or name"
                    className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="socialFollowing" className="block text-sm font-medium mb-2 text-white">
                Total Social Media Following
              </label>
              <input
                type="text"
                id="socialFollowing"
                name="socialFollowing"
                value={formData.socialFollowing}
                onChange={handleChange}
                placeholder="Approximate total followers across all platforms"
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white placeholder:text-white/40"
              />
            </div>

            <div>
              <label htmlFor="whyPartner" className="block text-sm font-medium mb-2 text-white">
                Why do you want to partner with Rides & Dives? *
              </label>
              <textarea
                id="whyPartner"
                name="whyPartner"
                value={formData.whyPartner}
                onChange={handleChange}
                required
                rows={4}
                maxLength={500}
                placeholder="Tell us why you'd be a great partner..."
                className="w-full px-4 py-3 bg-black border border-[#8B6F47]/50 rounded-lg focus:outline-none focus:border-[#8B6F47] text-white resize-none placeholder:text-white/40"
              ></textarea>
              <p className="text-sm text-white/50 mt-1">
                {formData.whyPartner.length}/500 characters
              </p>
            </div>

            {/* Consent to Contact Checkbox */}
            <div className="bg-[#8B6F47]/10 border border-[#8B6F47]/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setConsentToContact(!consentToContact)}
                  className={`w-6 h-6 flex-shrink-0 rounded border-2 flex items-center justify-center cursor-pointer transition-colors mt-0.5 ${
                    consentToContact ? 'bg-[#8B6F47] border-[#8B6F47]' : 'border-white/30 hover:border-white/50'
                  }`}
                >
                  {consentToContact && <i className="ri-check-line text-black text-sm"></i>}
                </button>
                <label 
                  className="text-sm text-white/80 cursor-pointer leading-relaxed"
                  onClick={() => setConsentToContact(!consentToContact)}
                >
                  I consent to being contacted by Rides &amp; Dives via text messages and phone calls at the number or email provided regarding my partner application and promotional offers. Message &amp; data rates may apply. I understand I can opt out at any time by replying STOP or contacting us directly. <span className="text-[#8B6F47]">*</span>
                </label>
              </div>
            </div>

            {formStatus === 'error' && (
              <p className="text-red-400 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                Please fill in all required fields and agree to the contact consent.
              </p>
            )}

            <button
              type="submit"
              disabled={formStatus === 'loading'}
              className="w-full bg-[#8B6F47] text-black font-bold py-4 px-8 rounded-full hover:bg-[#A08558] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {formStatus === 'loading' ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Submitting...
                </span>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-black py-20 border-t border-[#8B6F47]/20">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">Questions About Partnership?</h2>
          <p className="text-xl text-white/70 mb-8">
            We're here to help. Reach out to our partnership team.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 border-2 border-[#8B6F47] text-white text-sm tracking-widest uppercase rounded-full hover:bg-[#8B6F47] hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Contact Us
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] pt-12 md:pt-20 pb-8">
        <div className="px-4 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-16 mb-12 md:mb-16">
            <div>
              <div className="flex items-center mb-6 md:mb-8">
                <img 
                  src="https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/1d9414342dd58a00fdde5ee22dbe4d3e.png" 
                  alt="Rides & Dives" 
                  className="h-16 md:h-20 w-auto"
                />
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                130,000 miles exploring the best rides and dives across America. No filters. No bullshit. Just the road.
              </p>
            </div>
            <div>
              <h3 className="text-[#8B6F47] text-xs tracking-[3px] uppercase mb-6">Explore</h3>
              <div className="space-y-4">
                <Link to="/rides" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Rides</Link>
                <Link to="/dives" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Dives</Link>
                <Link to="/about" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">About</Link>
                <Link to="/contact" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Contact</Link>
                <Link to="/partners" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Become a Partner</Link>
              </div>
            </div>
            <div>
              <h3 className="text-[#8B6F47] text-xs tracking-[3px] uppercase mb-6">Follow the Journey</h3>
              <div className="space-y-4">
                <a href="https://www.youtube.com/@RidesDives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-2xl"></i>
                  <span className="text-base">YouTube</span>
                </a>
                <a href="https://www.instagram.com/rides.and.dives/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-2xl"></i>
                  <span className="text-base">Instagram</span>
                </a>
                <a href="https://www.tiktok.com/@ridesanddives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-tiktok-fill text-2xl"></i>
                  <span className="text-base">TikTok</span>
                </a>
                <a href="https://www.facebook.com/share/g/1AqygJbGTi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-2xl"></i>
                  <span className="text-base">Facebook</span>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-[#8B6F47]/30 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">© 2026 Rides & Dives. 130,000 miles and counting.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-white/70 text-sm hover:text-white transition-colors cursor-pointer">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
